import { Box, Stack, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * PageHero — opening band on every authenticated app surface.
 *
 * Mirrors the homepage step rhythm: a soft radial brand-tinted backdrop, an
 * optional eyebrow line, an h1 with `clamp(...)` scaling so it lives well at
 * 360px and at desktop widths, and an optional lede + right-aligned action.
 *
 * The hero is intentionally unopinionated about page width — wrap it in
 * whatever Container the route already uses.
 */
export function PageHero({
  eyebrow,
  title,
  lede,
  action,
  align = 'left',
  tinted = true,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  action?: ReactNode;
  align?: 'left' | 'center';
  /** When false, drops the radial brand backdrop (use on shells that already paint one). */
  tinted?: boolean;
}) {
  return (
    <Box
      style={{
        position: 'relative',
        paddingBlock: '32px',
        marginBottom: '8px',
        background: tinted
          ? 'light-dark(' +
            'linear-gradient(180deg, color-mix(in srgb, var(--mantine-color-sun-6) 18%, transparent) 0%, color-mix(in srgb, var(--wwaave-brand-primary) 9%, transparent) 40%, transparent 100%), ' +
            'linear-gradient(180deg, var(--wwaave-brand-wash) 0%, transparent 100%)' +
            '), ' +
            'light-dark(' +
            'radial-gradient(ellipse at top left, color-mix(in srgb, var(--wwaave-brand-primary) 14%, transparent) 0%, transparent 70%), ' +
            'radial-gradient(ellipse at top left, color-mix(in srgb, var(--wwaave-brand-primary) 8%, transparent) 0%, transparent 70%)' +
            ')'
          : undefined,
        borderRadius: '16px',
      }}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: action ? 'row' : 'column',
          gap: 24,
          alignItems: action ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
          justifyContent: action ? 'space-between' : 'flex-start',
          flexWrap: 'wrap',
          textAlign: align === 'center' ? 'center' : 'left',
        }}
      >
        <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
          {eyebrow ? (
            <Text
              size="sm"
              c="brand.7"
              fw={500}
              style={{ letterSpacing: '0.02em', textTransform: 'uppercase' }}
            >
              {eyebrow}
            </Text>
          ) : null}
          <Title
            order={1}
            style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.6rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
              margin: 0,
            }}
          >
            {title}
          </Title>
          {lede ? (
            <Text c="dimmed" size="lg" style={{ lineHeight: 1.5, maxWidth: 640 }}>
              {lede}
            </Text>
          ) : null}
        </Stack>
        {action ? <Box style={{ flexShrink: 0 }}>{action}</Box> : null}
      </Box>
    </Box>
  );
}
